/**
 * The generic conversation.
 *
 * One component for every messaging surface in the product: the office order thread,
 * the office internal-notes thread, the homeowner's order messages, and whatever comes
 * next (team chat, support tickets) — because a "channel" is the abstraction the API
 * exposes and none of those differ in anything but configuration.
 *
 * ```tsx
 * <ChannelView cid={buildChannelCid(CHANNEL_TYPE_ORDER_MESSAGE, orderId)} />
 * ```
 *
 * ## Interface vs implementation (Rule 6)
 *
 * A caller supplies a `cid`. Everything else is optional and narrows a full-featured
 * default: paginated thread, day separators, unread marker + read-cursor advance,
 * pinned strip, composer with uploads and typing, realtime refresh. The hidden
 * implementation is seven queries, three mutations, a realtime subscription and a
 * capability fold — none of which a screen should have to assemble again.
 *
 * ## What it does NOT own
 *
 * - **Which routes a visit card links to.** Order-domain UI arrives through
 *   `renderBlock` / `renderPromptBlock`.
 * - **Tabs, headers, badges.** A surface composes those around the view.
 * - **Presence.** The online dot is an order-scoped query in the office slice; pass
 *   `renderAvatar` to keep it.
 * - **The URL.** A deep link is read by the surface and handed over as `focusedMessage`;
 *   the view only resolves it (either id space) and scrolls to it.
 */

import { useGetAuthMe } from "@birdwatch/client";
import { cn } from "cn";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeRefresh } from "@/features/shared/realtime-refresh";
import useTimezone from "@/hooks/use-timezone";
import { DEFAULT_TIME_ZONE } from "@/libs/date-fns";
import { useChannel } from "./_api/use-channel";
import { useChannelMessages } from "./_api/use-channel-messages";
import { useChannelPromptBlocks } from "./_api/use-channel-prompt-blocks";
import { useChannelUnread, useMarkChannelRead } from "./_api/use-channel-read";
import { ChannelComposer } from "./_components/channel-composer";
import { ChannelMessageDeleteDialog } from "./_components/channel-message-delete-dialog";
import { ChannelMessageList } from "./_components/channel-message-list";
import { ChannelMessageSkeleton } from "./_components/channel-message-skeleton";
import { ChannelMessageUnpinDialog } from "./_components/channel-message-unpin-dialog";
import { ChannelPromptBlockBar } from "./_components/channel-prompt-block-bar";
import { ChannelScrollToBottom } from "./_components/channel-scroll-to-bottom";
import { ChannelTyping } from "./_components/channel-typing";
import { useChannelMessageInteractions } from "./_hooks/use-channel-message-interactions";
import { useMarkReadOnView } from "./_hooks/use-mark-read-on-view";
import { useQuotedReply } from "./_hooks/use-quoted-reply";
import { useResolvedMessageTarget } from "./_hooks/use-resolved-message-target";
import { scrollChannelToBottom } from "./_libs/channel-scroll";
import type { ChannelMessageTarget } from "./_libs/message-target";
import { getNewestMessageId } from "./_libs/newest-message-id";
import { computeReadOnViewTarget } from "./_libs/read-on-view-target";
import type { ReadCursorId } from "./_libs/unread-boundary";
import {
  type ChannelDaySeparatorRenderer,
  type ChannelMessageRenderer,
  type ChannelNewDividerRenderer,
  type ChannelSurfaceConfig,
  resolveChannelSurface,
} from "./_types/channel-surface";

export type ChannelViewProps = {
  /** `<channelTypeId>:<entityId>` — build it with `buildChannelCid`. */
  cid: string;
  /** What this screen wants. Every key optional; the channel's own flags narrow it. */
  config?: ChannelSurfaceConfig;
  /**
   * Scroll to and flash one message. Accepts the int `Message.id` or the legacy 24-char
   * `oldId` every pre-channel deep link carries — parse the query param with
   * `parseMessageTargetParam`. The view pages backwards while the target is missing.
   *
   * To ask for the SAME message a second time (a search result clicked twice), pass a
   * target with a fresh `nonce`; an unchanged object is correctly a no-op.
   */
  focusedMessage?: ChannelMessageTarget | null;
  /**
   * Swaps the message bubble (and its day heading) for another UI without touching data,
   * scrolling, pagination, drafts, realtime or the composer. Omitted, this renders exactly
   * what it rendered before the seam existed — see `@/features/shared/conversation`.
   */
  renderMessage?: ChannelMessageRenderer;
  renderDaySeparator?: ChannelDaySeparatorRenderer;
  renderNewDivider?: ChannelNewDividerRenderer;
};

export function ChannelView({
  cid,
  config,
  focusedMessage,
  renderMessage,
  renderDaySeparator,
  renderNewDivider,
}: ChannelViewProps) {
  const { data: me } = useGetAuthMe().get();
  const { timeZone } = useTimezone();

  // Read off the RAW config, not `surface`: the resolver folds the channel's own
  // features into the surface, so it cannot run until the row this decides how to fetch
  // has already been fetched.
  const { data: channel } = useChannel(cid, {
    metadata: config?.includeMetadata,
  });
  const surface = useMemo(
    () => resolveChannelSurface(config, channel?.features),
    [config, channel?.features],
  );
  // `"classic"` puts the composer on top of a natural-height, newest-first thread (the
  // member order thread); the default `"chat"` composer sits below a reversed scroller.
  const classic = surface.layout === "classic";

  // Live: the conversation's events (`message.changed`, `comment.changed`, the
  // `visit.changed` its visit cards read) ride the entity it hangs off — the order of an
  // order thread, the home of a support conversation. Subscribed here rather than left
  // to a provider around the screen: the member Chats and the Office Inbox show a
  // conversation outside any order's provider. A screen already listening on the same
  // channel costs nothing extra — the one refresh queue coalesces both.
  useRealtimeRefresh({
    channels: [channel?.entityId],
    enabled: surface.realtime,
  });

  const {
    data: messages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useChannelMessages(cid, {
    pageSize: surface.pageSize,
    rootsOnly: surface.rootsOnly,
  });

  // The read cursor drives two things, only one of which is the auto-advance: a surface
  // that clears the badge manually (the office) still needs the cursor for the per-bubble
  // dots on any channel type with no audience.
  const needsReadCursor = surface.markReadOnView || surface.unreadDots;
  const { data: unread, isFetching: isUnreadFetching } = useChannelUnread(
    cid,
    needsReadCursor,
  );
  const { data: prompts } = useChannelPromptBlocks(cid, surface.promptBlocks);

  // Tri-state, keyed off the QUERY rather than the field: the response's
  // `lastReadMessageId` is optional as well as nullable, so `?? null` alone would read a
  // still-loading cursor as "read nothing" and light the whole thread.
  const lastReadMessageId: ReadCursorId = unread
    ? (unread.lastReadMessageId ?? null)
    : undefined;

  // Authoritative per-message unread for a channel that has an audience (every order
  // thread). `undefined` — not `[]` — when the channel type has none, which is what tells
  // the calc to fall back to the personal cursor rather than reading "nothing unread".
  const audienceUnreadIds = unread?.audienceUnread?.messageIds;

  // The "New" marker latches its answer once and holds it for the visit, so it must not
  // read a cached cursor from the PREVIOUS visit as the server's verdict — a reopened
  // conversation renders that stale "caught up" state before revalidating.
  const unreadSettled = !!unread && !isUnreadFetching;

  const { markRead } = useMarkChannelRead(cid);
  const interactions = useChannelMessageInteractions(cid);

  // "Quote reply": the composer's pending quote, the list's action, and the quote card's jump.
  const {
    quotedMessage,
    changeQuotedMessage,
    replyToMessage,
    openQuotedMessage,
    target,
  } = useQuotedReply({ scopeKey: cid, focusedMessage });

  // The jump is held as a REQUEST, not an id: one message can be asked for twice (two
  // clicks on one search result) and only the nonce tells those apart.
  const [focusRequest, setFocusRequest] = useState<{
    id: string;
    nonce: number;
  } | null>(null);

  // The scroll container itself, attached by `ChannelMessageList`. Backwards pagination
  // is NOT driven from here — the list observes its own old end (`useLoadOlderOnVisible`),
  // because a trigger wired up here would be attached before the container it needs
  // exists. What is left needs the node: the deep-link jump, the scroll-to-bottom button,
  // and the jump on own send.
  const containerRef = useRef<HTMLDivElement>(null);

  // A deep link may name a message that is pages back; the hook keeps asking for older
  // pages until it lands (bounded), then this effect hands the resolved int id to the
  // list. Kept in state rather than derived so a later pin-jump can override it.
  const deepLinkedMessage = useResolvedMessageTarget({
    target,
    messages,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const deepLinkedMessageId = deepLinkedMessage?.id ?? null;
  // A deep link carries no nonce; 0 is then a constant and this behaves exactly as it
  // did before the nonce existed.
  const focusNonce = target?.nonce ?? 0;

  useEffect(() => {
    if (deepLinkedMessageId != null) {
      setFocusRequest({ id: deepLinkedMessageId, nonce: focusNonce });
    }
  }, [deepLinkedMessageId, focusNonce]);

  const newestMessageId = getNewestMessageId(messages);

  useMarkReadOnView({
    enabled: surface.markReadOnView && !isLoading,
    // A roots-only list holds no replies, and the cursor spans them too.
    target: computeReadOnViewTarget({
      coversWholeChannel: !surface.rootsOnly,
      newestMessageId,
      lastReadMessageId,
      unreadCount: unread?.unreadCount,
    }),
    delayMs: surface.markReadDelayMs,
    markRead,
  });

  // Every child needs the channel row (its features decide what renders at all), so
  // the whole view waits on it rather than each child guarding independently.
  if (!channel) {
    return (
      <div
        data-test="channel-view"
        className={cn(
          /* sorted */
          classic ? "flex flex-col" : "flex flex-col flex-1 overflow-hidden",
          surface.className,
        )}
      >
        {/* Same band as the message column this skeleton stands in for, so the placeholder
            does not start full width and jump to the capped width once the channel lands. */}
        <div className={cn("w-full mx-auto", surface.contentClassName)}>
          <ChannelMessageSkeleton />
        </div>
      </div>
    );
  }

  // The composer mounts only on a channel that takes new messages (`acceptsMessages`: an
  // archived homeowner thread does not, the team's archived notes do); Reply writes into
  // it, so it is offered exactly when the composer is there to receive the quote.
  const hasComposer = surface.composer !== false && channel.acceptsMessages;

  const messageList = (
    <ChannelMessageList
      cid={cid}
      channel={channel}
      messages={messages}
      containerRef={containerRef}
      timeZone={timeZone || DEFAULT_TIME_ZONE}
      viewerId={me?.id}
      viewerRoles={me?.roles}
      surface={surface}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      lastReadMessageId={lastReadMessageId}
      audienceUnreadIds={audienceUnreadIds}
      focusedMessageId={focusRequest?.id ?? null}
      focusNonce={focusRequest?.nonce ?? 0}
      editingMessageId={interactions.editingMessageId}
      onCancelEdit={interactions.cancelEdit}
      onEditMessage={interactions.startEdit}
      editingQuote={interactions.editingQuote}
      onEditingQuoteChange={interactions.changeEditingQuote}
      onDeleteMessage={interactions.requestDelete}
      onTogglePin={interactions.togglePin}
      // An open comment editor takes the quote; otherwise Reply starts one in the composer.
      onReplyMessage={
        hasComposer
          ? (message) => {
              if (!interactions.quoteIntoEdit(message)) replyToMessage(message);
            }
          : undefined
      }
      onOpenQuotedMessage={openQuotedMessage}
      renderMessage={renderMessage}
      renderDaySeparator={renderDaySeparator}
      renderNewDivider={renderNewDivider}
      unreadSettled={unreadSettled}
    />
  );

  // The footer and the composer ride the same band as the message column above them,
  // and they get there the same way it does: a FULL-WIDTH box that reserves the
  // scroller's bar, with the band capped inside it.
  //
  // `scrollbar-gutter-match` is the other half of the list's `scrollbar-gutter-stable`:
  // the scroller reserves its bar's width inside its own content box, and these two
  // reserve the identical width although nothing scrolls them, so the two capped
  // columns centre on one axis and line up to the pixel whether the bar is showing or
  // not. Where scrollbars overlay (macOS, touch) both reserve nothing. See `global.css`.
  const footerBlock = surface.footer ? (
    <div className="w-full scrollbar-gutter-match">
      <div className={cn("w-full mx-auto", surface.contentClassName)}>
        {surface.footer}
      </div>
    </div>
  ) : null;

  const composerBlock = (
    <div className="relative shrink-0 w-full">
      {/* The jump-to-newest button, the typing strip and the surface's `floating` content
          (the office tone detector) hang OUTSIDE this block, so they cannot live in a
          gutter box — `scrollbar-gutter-match` is `overflow-y: auto`, and it would clip
          them (the footer's box did, and hid the tone button). They ride their own layer
          instead, pinned to the edge they hang off: the top for the chat layout, the
          bottom for `classic`, whose composer sits above the thread. The layer is left at
          ZERO height on purpose — a stretched one would cover the composer and swallow
          its clicks.

          Having no overflow of its own, the layer cannot reserve the gutter, so where a
          bar takes space it rides half a bar right of the composer's band. Nothing aligns
          to it and it never moves, which is the one place that is cheaper than the
          pointer-events juggling the alternative needs. */}
      <div
        className={cn(
          /* sorted */
          "absolute inset-x-0",
          "w-full",
          "mx-auto",
          classic ? "bottom-0" : "top-0",
          surface.contentClassName,
        )}
      >
        {/* Scroll-to-bottom only makes sense for the reversed chat layout. */}
        {!classic && <ChannelScrollToBottom containerRef={containerRef} />}
        {surface.typing && surface.realtime && (
          <ChannelTyping
            // Remount on channel change — a typist who left never sends a stop.
            key={cid}
            cid={cid}
            channel={channel}
            className={cn(
              "absolute right-0 left-0 px-3 xl:px-4 pt-1 bg-bg-light",
              // `classic` composer is on top, so typing shows beneath it, not above.
              classic ? "-bottom-3" : "-top-3",
            )}
          />
        )}
        {surface.floating}
      </div>
      {/* A surface withholds the composer for its own reasons (`composer: false`) and
          puts its own explanation in `footer`; the channel's own state is the floor —
          a channel that refuses the write gets no composer, whatever the surface asked
          for. */}
      {!hasComposer ? null : (
        <div className="w-full scrollbar-gutter-match">
          <div
            className={cn(
              /* sorted */
              "flex",
              "flex-col",
              "w-full",
              "p-3 mx-auto",
              "xl:p-4",
              surface.contentClassName,
            )}
          >
            <ChannelComposer
              // The editor seeds its content from the draft ONCE per mount
              // (`useChatComposerForm` keeps an `initialRef`), so a surface that swaps the
              // cid in place — the office aside moving between orders — would keep the
              // previous channel's half-written text on screen and post it here.
              key={cid}
              cid={cid}
              surface={surface}
              quotedMessage={quotedMessage}
              onQuotedMessageChange={changeQuotedMessage}
              // Own send jumps to the newest message: the bottom of the reversed chat
              // list, or the top of the classic list (newest sits under the composer).
              onSent={
                classic
                  ? () => containerRef.current?.scrollTo({ top: 0 })
                  : () => scrollChannelToBottom(containerRef.current)
              }
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      data-test="channel-view"
      data-channel-cid={cid}
      className={cn(
        classic ? "flex flex-col" : "flex flex-col flex-1 min-h-0",
        surface.className,
      )}
    >
      {surface.header}

      {surface.promptBlocks && (
        <ChannelPromptBlockBar
          channel={channel}
          prompts={prompts}
          renderPromptBlock={surface.renderPromptBlock}
        />
      )}

      {classic ? (
        // Composer (and the closed-order footer that stands in for it) sits on top, above
        // the newest-first thread — the member order thread's historical layout.
        <>
          {composerBlock}
          {footerBlock}
          {messageList}
        </>
      ) : (
        <>
          {messageList}
          {footerBlock}
          {composerBlock}
        </>
      )}

      <ChannelMessageDeleteDialog
        message={interactions.messagePendingDelete}
        open={interactions.messagePendingDelete != null}
        onOpenChange={(open) => {
          if (!open) interactions.dismissDelete();
        }}
        isDeleting={interactions.isDeleting}
        onConfirm={interactions.deleteMessage}
        deleteConfirm={surface.messageActions.deleteConfirm}
      />

      <ChannelMessageUnpinDialog
        message={interactions.messagePendingUnpin}
        open={interactions.messagePendingUnpin != null}
        onOpenChange={(open) => {
          if (!open) interactions.dismissUnpin();
        }}
        isUnpinning={interactions.isUnpinning}
        onConfirm={interactions.unpinMessage}
        unpinConfirm={surface.messageActions.unpinConfirm}
      />
    </div>
  );
}
